import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import InputMask from "react-input-mask";
import {
  FaArrowLeft,
  FaCheck,
  FaCheckCircle,
  FaCamera,
  FaIdCard,
  FaPen,
  FaSyncAlt,
  FaTrashAlt,
  FaUpload,
  FaUser,
} from "react-icons/fa";

import { DocumentTypeContext } from "../../context/DocumentContext";
import { UserTypeContext } from "../../context/UserTypeContext";
import { AuthContext } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import "./SignaturePage.css";

type SignatureMode = "type" | "draw";
type CameraFacing = "user" | "environment";

interface FontStyleOption {
  fontFamily: string;
  label: string;
}

const fontStyles: FontStyleOption[] = [
  { fontFamily: "'Dancing Script'", label: "Clássica" },
  { fontFamily: "'Pacifico'", label: "Elegante" },
  { fontFamily: "'Great Vibes'", label: "Formal" },
];

const SignaturePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const {
    formValuesSign,
    setFormValuesSign,
    createSignUser,
    signers,
    document: documentData,
    infoSign,
    setInfoSign,
    getDocumentSign,
    getSigners,
  } = useContext(DocumentTypeContext);

  const { formValuesUsers, ipUser, getUsersById, getUserIp } =
    useContext(UserTypeContext);
  const { decryptToken } = useContext(AuthContext);

  const [signatureMode, setSignatureMode] = useState<SignatureMode>("type");
  const [selectedStyle, setSelectedStyle] = useState<FontStyleOption | null>(
    fontStyles[0],
  );
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<number[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraFacing, setCameraFacing] = useState<CameraFacing>("user");

  const sigCanvas = useRef<any>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const url = window.location.href;
  const urlObj = useMemo(() => new URL(url), [url]);
  const tokenParam = urlObj.searchParams.get("token");
  const documentIdParam = urlObj.searchParams.get("documentId");

  const goBackToDocument = () => {
    const params = new URLSearchParams(location.search);
    navigate({
      pathname: "/admin/sign-document",
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  useEffect(() => {
    const ensureContext = async () => {
      if (documentData && signers?.length > 0 && formValuesUsers?.email) {
        return;
      }

      setBootstrapping(true);
      try {
        if (tokenParam) {
          const decoded = decodeURIComponent(tokenParam).replace(/ /g, "+");
          const result = await decryptToken(decoded);
          if (result?.data?.data) {
            const { documentId, userId } = result.data.data;
            setInfoSign(result.data.data);
            await Promise.all([
              getDocumentSign(documentId, true),
              getSigners(documentId, true),
              getUsersById(userId, true),
            ]);
            await getUserIp();
          } else {
            showToast("error", "Token inválido. Volte e tente novamente.");
            goBackToDocument();
          }
        } else if (documentIdParam) {
          showToast(
            "info",
            "Carregue o documento antes de assinar.",
          );
          goBackToDocument();
        } else {
          goBackToDocument();
        }
      } catch (error) {
        showToast("error", "Erro ao carregar dados de assinatura.");
        goBackToDocument();
      } finally {
        setBootstrapping(false);
      }
    };

    ensureContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const foundSigner = useMemo(
    () => signers?.find?.((x: any) => x.name === formValuesUsers.email),
    [signers, formValuesUsers.email],
  );

  useEffect(() => {
    if (foundSigner?.hasSigned) {
      const params = new URLSearchParams(location.search);
      navigate({
        pathname: "/admin/sign-document/concluido",
        search: params.toString() ? `?${params.toString()}` : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundSigner]);

  useEffect(() => {
    if (signatureMode !== "type") return;
    if (!selectedStyle) return;
    applyStyleToCanvas(selectedStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureMode, selectedStyle, formValuesUsers.name]);

  // Conecta o stream ao <video> quando ele entra em tela e a cada troca de
  // camera: o stream anterior e substituido, entao o srcObject precisa ser
  // reatribuido.
  useEffect(() => {
    if (!cameraOpen) return;
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    video.srcObject = streamRef.current;
    video.play().catch(() => undefined);
  }, [cameraOpen, cameraFacing]);

  // Garante que a camera seja desligada ao sair da tela
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (signatureMode === "type" && selectedStyle) {
        applyStyleToCanvas(selectedStyle);
      } else if (sigCanvas.current && canvasWrapperRef.current) {
        const canvas = sigCanvas.current.getCanvas?.();
        if (canvas) {
          canvas.width = canvasWrapperRef.current.clientWidth;
          canvas.height = 200;
        }
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureMode, selectedStyle]);

  const handleBlur = (value: string, name: string) => {
    setFormValuesSign({
      ...formValuesSign,
      [name]: value,
    });
  };

  const applyStyleToCanvas = (style: FontStyleOption) => {
    if (!sigCanvas.current) return;
    const canvas = sigCanvas.current.getCanvas?.();
    if (!canvas) return;

    const wrapperWidth = canvasWrapperRef.current?.clientWidth || 500;
    canvas.width = wrapperWidth;
    canvas.height = 200;

    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#111827";
    context.textAlign = "center";
    context.textBaseline = "middle";

    let fontSize = 64;
    context.font = `${fontSize}px ${style.fontFamily}`;

    const words = (formValuesUsers.name || "Sua Assinatura").split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine + word + " ";
      const testWidth = context.measureText(testLine).width;
      if (testWidth > canvas.width - 60 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word + " ";
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    while (lines.length * fontSize > canvas.height - 30) {
      fontSize -= 2;
      context.font = `${fontSize}px ${style.fontFamily}`;
    }

    let y = (canvas.height - lines.length * fontSize) / 2 + fontSize / 2;
    for (const line of lines) {
      context.fillText(line.trim(), canvas.width / 2, y);
      y += fontSize;
    }
  };

  const clearDrawing = () => {
    sigCanvas.current?.clear?.();
    setHasDrawnSignature(false);
  };

  const switchToDraw = () => {
    setSignatureMode("draw");
    setSelectedStyle(null);
    setTimeout(() => {
      sigCanvas.current?.clear?.();
      const canvas = sigCanvas.current?.getCanvas?.();
      if (canvas && canvasWrapperRef.current) {
        canvas.width = canvasWrapperRef.current.clientWidth;
        canvas.height = 200;
      }
      setHasDrawnSignature(false);
    }, 50);
  };

  const switchToType = (style: FontStyleOption) => {
    setSignatureMode("type");
    setSelectedStyle(style);
    setHasDrawnSignature(false);
  };

  const base64ToByteArray = (base64: string) => {
    const binaryString = atob(base64);
    const byteArray = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
    }
    return Array.from(byteArray);
  };

  const handleFileSelected = (file?: File) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        const base64String = reader.result.toString().split(",")[1];
        setSelectedImage(base64ToByteArray(base64String));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelected(event.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelected(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const clearImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
  };

  const handleFileUpload = () => {
    const fileInput = window.document.getElementById(
      "signaturePageFileInput",
    ) as HTMLInputElement;
    fileInput?.click();
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const handleOpenCamera = async (mode: CameraFacing = cameraFacing) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast(
        "info",
        "Seu navegador nao suporta a camera. Anexe uma foto da sua selfie com o documento.",
      );
      return;
    }

    const wasOpen = cameraOpen;
    setCameraStarting(true);
    try {
      // Libera o stream anterior antes de pedir o outro lado: manter os dois
      // abertos trava a camera em boa parte dos celulares.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      const stream = await navigator.mediaDevices.getUserMedia({
        // Padrao frontal: a etapa pede uma selfie segurando o documento.
        video: { facingMode: mode },
        audio: false,
      });
      streamRef.current = stream;
      setCameraFacing(mode);
      setCameraOpen(true);
    } catch (error) {
      // Numa troca que falha (aparelho com uma camera so) a tela ficaria aberta
      // sem imagem, porque o stream anterior ja foi liberado: fecha a camera e
      // deixa o usuario reabrir ou anexar um arquivo.
      stopCamera();
      showToast(
        "info",
        wasOpen
          ? "Nao foi possivel trocar de camera neste aparelho."
          : "Nao foi possivel acessar a camera. Verifique a permissao ou anexe uma foto da sua selfie com o documento.",
      );
    } finally {
      setCameraStarting(false);
    }
  };

  const handleSwitchCamera = () => {
    handleOpenCamera(cameraFacing === "user" ? "environment" : "user");
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = window.document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        handleFileSelected(
          new File([blob], "captura.jpg", { type: "image/jpeg" }),
        );
        stopCamera();
      },
      "image/jpeg",
      0.92,
    );
  };

  const validate = () => {
    if (!formValuesSign?.nationalRegisterCode) {
      showToast("info", "Informe seu CPF para continuar.");
      return false;
    }
    const cpfDigits = (formValuesSign.nationalRegisterCode || "").replace(
      /\D/g,
      "",
    );
    if (cpfDigits.length !== 11) {
      showToast("info", "CPF incompleto. Verifique e tente novamente.");
      return false;
    }
    if (foundSigner?.requireSignerDocument && !selectedImage) {
      showToast("info", "Envie uma selfie segurando seu documento de identificação.");
      return false;
    }
    if (signatureMode === "draw" && !hasDrawnSignature) {
      showToast("info", "Desenhe sua assinatura no campo indicado.");
      return false;
    }
    if (signatureMode === "type" && !selectedStyle) {
      showToast("info", "Escolha um estilo para sua assinatura.");
      return false;
    }
    if (!agreed) {
      showToast(
        "info",
        "Confirme que você leu o documento e aceita assinar.",
      );
      return false;
    }
    return true;
  };

  const buildSignatureDataURL = (): string | null => {
    if (signatureMode === "type" && selectedStyle) {
      const tempCanvas = window.document.createElement("canvas");
      const tempContext = tempCanvas.getContext("2d");
      if (!tempContext) return null;

      tempCanvas.width = 500;
      tempCanvas.height = 200;
      tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempContext.fillStyle = "#000";
      tempContext.textAlign = "center";
      tempContext.textBaseline = "middle";

      let fontSize = 80;
      tempContext.font = `${fontSize}px ${selectedStyle.fontFamily}`;

      const name = formValuesUsers.name || "";
      const words = name.split(" ");
      const lines: string[] = [];
      let currentLine = "";

      for (const word of words) {
        const testLine = currentLine + word + " ";
        const testWidth = tempContext.measureText(testLine).width;
        if (testWidth > tempCanvas.width - 40 && currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = word + " ";
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine.trim());

      while (lines.length * fontSize > tempCanvas.height - 20) {
        fontSize -= 2;
        tempContext.font = `${fontSize}px ${selectedStyle.fontFamily}`;
      }

      let y =
        (tempCanvas.height - lines.length * fontSize) / 2 + fontSize / 2;
      for (const line of lines) {
        tempContext.fillText(line.trim(), tempCanvas.width / 2, y);
        y += fontSize;
      }

      return tempCanvas.toDataURL("image/png");
    }

    if (signatureMode === "draw" && sigCanvas.current) {
      try {
        return sigCanvas.current.getTrimmedCanvas().toDataURL("image/png");
      } catch (err) {
        return sigCanvas.current.toDataURL("image/png");
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const dataURL = buildSignatureDataURL();
    if (!dataURL) {
      showToast("error", "Não foi possível gerar a sua assinatura.");
      return;
    }

    try {
      setSigning(true);
      // @ts-ignore selectedImage carries number[] (legacy contract)
      await createSignUser(dataURL, selectedImage);
      const params = new URLSearchParams(location.search);
      navigate({
        pathname: "/admin/sign-document/concluido",
        search: params.toString() ? `?${params.toString()}` : "",
      });
    } catch (error) {
      // toast já foi exibido em createSignUser
      console.error(error);
    } finally {
      setSigning(false);
    }
  };

  if (bootstrapping) {
    return (
      <div className="signpage-loading">
        <div className="signpage-loading__spinner" />
        <span>Carregando dados de assinatura...</span>
      </div>
    );
  }

  return (
    <div className="signpage">
      <header className="signpage__topbar">
        <button
          type="button"
          className="signpage__back"
          onClick={goBackToDocument}
          disabled={signing}
        >
          <FaArrowLeft />
          <span>Voltar ao documento</span>
        </button>
        <div className="signpage__topbar-title">
          <span className="signpage__topbar-eyebrow">Assinatura digital</span>
          <strong>{infoSign?.documentName || "Documento"}</strong>
        </div>
        <div className="signpage__topbar-spacer" />
      </header>

      <main className="signpage__main">
        <section className="signpage__intro">
          <span className="signpage__intro-eyebrow">
            <FaPen /> Etapa final
          </span>
          <h1 className="signpage__intro-title">Assine seu documento</h1>
          <p className="signpage__intro-subtitle">
            Confira seus dados, escolha o estilo da sua assinatura e finalize
            com segurança. Sua assinatura tem validade jurídica.
          </p>
        </section>

        <article className="signpage__card">
          <div className="signpage__step">
            <div className="signpage__step-header">
              <span className="signpage__step-badge">1</span>
              <div>
                <h2 className="signpage__step-title">
                  <FaUser /> Confirme seus dados
                </h2>
                <p className="signpage__step-desc">
                  Estes são os dados que serão registrados na assinatura.
                </p>
              </div>
            </div>

            <div className="signpage__info">
              <div className="signpage__info-item">
                <span className="signpage__info-label">Nome</span>
                <span
                  className="signpage__info-value"
                  title={formValuesUsers.name}
                >
                  {formValuesUsers.name || "—"}
                </span>
              </div>
              <div className="signpage__info-item">
                <span className="signpage__info-label">E-mail</span>
                <span
                  className="signpage__info-value"
                  title={formValuesUsers.email}
                >
                  {formValuesUsers.email || "—"}
                </span>
              </div>
              <div className="signpage__info-item">
                <span className="signpage__info-label">IP</span>
                <span className="signpage__info-value">{ipUser || "—"}</span>
              </div>
            </div>

            <label className="signpage__field-label" htmlFor="cpf-input">
              CPF <span className="signpage__required">*</span>
            </label>
            <InputMask
              mask="999.999.999-99"
              value={formValuesSign?.nationalRegisterCode || ""}
              onChange={(e) =>
                handleBlur(e.target.value, "nationalRegisterCode")
              }
              className="signpage__input"
              name="nationalRegisterCode"
              id="cpf-input"
              type="text"
              placeholder="000.000.000-00"
              disabled={signing}
            />
          </div>

          {foundSigner?.requireSignerDocument && (
            <div className="signpage__step">
              <div className="signpage__step-header">
                <span className="signpage__step-badge">2</span>
                <div>
                  <h2 className="signpage__step-title">
                    <FaIdCard /> Selfie com seu documento
                  </h2>
                  <p className="signpage__step-desc">
                    Tire uma foto do seu rosto segurando seu RG ou CNH ao lado.
                    O documento precisa estar aberto e legível na imagem.
                  </p>
                </div>
              </div>

              {cameraOpen ? (
                <div className="signpage__camera">
                  <video
                    ref={videoRef}
                    className="signpage__camera-video"
                    playsInline
                    muted
                    autoPlay
                  />
                  <div className="signpage__camera-actions">
                    <button
                      type="button"
                      className="signpage__camera-shot"
                      onClick={handleCapturePhoto}
                      disabled={cameraStarting}
                    >
                      <FaCamera /> Tirar foto
                    </button>
                    <button
                      type="button"
                      className="signpage__camera-switch"
                      onClick={handleSwitchCamera}
                      disabled={cameraStarting}
                      title={
                        cameraFacing === "user"
                          ? "Usar câmera traseira"
                          : "Usar câmera frontal"
                      }
                    >
                      <FaSyncAlt />
                      {cameraFacing === "user" ? "Traseira" : "Frontal"}
                    </button>
                    <button
                      type="button"
                      className="signpage__camera-cancel"
                      onClick={stopCamera}
                      disabled={cameraStarting}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : !previewUrl ? (
                <>
                  <button
                    type="button"
                    className="signpage__camera-open"
                    onClick={() => handleOpenCamera("user")}
                    disabled={cameraStarting}
                  >
                    <FaCamera />
                    {cameraStarting ? "Abrindo câmera..." : "Tirar selfie agora"}
                  </button>

                  <div className="signpage__upload-divider">
                    <span>ou envie uma foto que já tirou</span>
                  </div>

                  <div
                    className={`signpage__upload ${
                      isDragging ? "signpage__upload--dragging" : ""
                    }`}
                    onClick={handleFileUpload}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="signpage__upload-icon">
                      <FaUpload />
                    </div>
                    <p className="signpage__upload-title">
                      {isDragging
                        ? "Solte a foto aqui"
                        : "Clique ou arraste sua selfie com o documento"}
                    </p>
                    <p className="signpage__upload-hint">
                      Formatos aceitos: PNG ou JPG
                    </p>

                    <input
                      type="file"
                      accept="image/*"
                      id="signaturePageFileInput"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </>
              ) : (
                <div className="signpage__preview">
                  <img
                    src={previewUrl}
                    alt="Prévia da selfie com o documento"
                    className="signpage__preview-img"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    className="signpage__preview-remove"
                    aria-label="Remover foto"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="signpage__step">
            <div className="signpage__step-header">
              <span className="signpage__step-badge">
                {foundSigner?.requireSignerDocument ? 3 : 2}
              </span>
              <div>
                <h2 className="signpage__step-title">
                  <FaPen /> Sua assinatura
                </h2>
                <p className="signpage__step-desc">
                  Escolha um estilo pronto ou desenhe sua própria assinatura.
                </p>
              </div>
            </div>

            <div className="signpage__tabs">
              <button
                type="button"
                className={`signpage__tab ${
                  signatureMode === "type" ? "signpage__tab--active" : ""
                }`}
                onClick={() => switchToType(selectedStyle ?? fontStyles[0])}
                disabled={signing}
              >
                Estilo pronto
              </button>
              <button
                type="button"
                className={`signpage__tab ${
                  signatureMode === "draw" ? "signpage__tab--active" : ""
                }`}
                onClick={switchToDraw}
                disabled={signing}
              >
                Desenhar
              </button>
            </div>

            {signatureMode === "type" && (
              <div className="signpage__styles">
                {fontStyles.map((style) => {
                  const isActive =
                    selectedStyle?.fontFamily === style.fontFamily;
                  return (
                    <button
                      type="button"
                      key={style.fontFamily}
                      onClick={() => switchToType(style)}
                      className={`signpage__style ${
                        isActive ? "signpage__style--active" : ""
                      }`}
                      disabled={signing}
                    >
                      {isActive && (
                        <span className="signpage__style-check">
                          <FaCheck />
                        </span>
                      )}
                      <span
                        className="signpage__style-preview"
                        style={{ fontFamily: style.fontFamily }}
                      >
                        {formValuesUsers.name?.split(" ")[0] || "Assinatura"}
                      </span>
                      <span className="signpage__style-label">
                        {style.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="signpage__canvas-block">
              <div className="signpage__canvas-header">
                <span className="signpage__canvas-title">
                  Pré-visualização
                </span>
                {signatureMode === "draw" && (
                  <button
                    type="button"
                    onClick={clearDrawing}
                    className="signpage__clear-btn"
                    disabled={signing}
                  >
                    <FaTrashAlt size={10} /> Limpar
                  </button>
                )}
              </div>
              <div className="signpage__canvas-wrapper" ref={canvasWrapperRef}>
                <SignatureCanvas
                  // @ts-ignore
                  ref={sigCanvas}
                  penColor="#111827"
                  onEnd={() => setHasDrawnSignature(true)}
                  canvasProps={{ className: "signpage__canvas" }}
                />
                {signatureMode === "draw" && !hasDrawnSignature && (
                  <span className="signpage__canvas-hint">
                    Desenhe aqui com o dedo ou mouse
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="signpage__agree">
            <label className="signpage__agree-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={signing}
              />
              <span>
                Confirmo que li e estou de acordo com o conteúdo do documento.
                Reconheço que esta assinatura tem validade jurídica.
              </span>
            </label>
          </div>
        </article>

        <p className="signpage__legal">
          Ao assinar, registramos data, hora, IP, localização e os dados
          informados acima. Esses dados garantem a autenticidade da assinatura.
        </p>
      </main>

      <footer className="signpage__footer">
        <div className="signpage__footer-inner">
          <button
            type="button"
            className="signpage__btn signpage__btn--ghost"
            onClick={goBackToDocument}
            disabled={signing}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="signpage__btn signpage__btn--primary"
            onClick={handleSubmit}
            disabled={signing}
          >
            {signing ? (
              <>
                <span className="signpage__btn-spinner" />
                Assinando...
              </>
            ) : (
              <>
                <FaCheckCircle /> Assinar Documento
              </>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default SignaturePage;
